// Account page — profile fields + subscription/credit summary + recent usage.
//
// Plan tier + credit balance come from Supabase (read via useCredits); the
// "Recent activity" feed reads the user's own credit_transactions rows
// directly (RLS scopes to auth.uid()).

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCredits';
import { PLANS } from '@/hooks/useSubscription';
import { Logo } from '@/components/feature/Logo';
import {
  API_BASE,
  getAuthHeaders,
  getPhoneStatus,
  registerPhone,
  verifyPhone,
  unlinkPhone,
  type PhoneStatus,
} from '@/lib/api';

interface CreditTx {
  id: string;
  amount: number;
  type: 'grant' | 'purchase' | 'spend' | 'refund';
  status: 'reserved' | 'confirmed' | 'refunded';
  note: string | null;
  created_at: string;
}

// Friendly plan label + supporting metadata for any plan_tier value.
function getPlanInfo(planTier: string) {
  if (planTier === 'free') {
    return {
      label: 'Free',
      price: 0,
      creditsPerMonth: 0,
      description: 'Start a free trial to unlock 20 credits + full plan access.',
      icon: 'ri-seedling-line',
      iconBg: 'bg-surface-dark/40 text-dark-muted',
    };
  }
  const plan = PLANS.find((p) => p.id === planTier);
  return {
    label: plan?.name ?? planTier,
    price: plan?.price ?? 0,
    creditsPerMonth: plan?.creditsPerMonth ?? 0,
    description: plan?.description ?? '',
    icon: planTier === 'business' ? 'ri-building-line'
      : planTier === 'pro' ? 'ri-vip-crown-line'
      : 'ri-rocket-line',
    iconBg: 'bg-primary/10 text-primary',
  };
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} days ago`;
  return new Date(iso).toLocaleDateString();
}

function describeTx(tx: CreditTx): string {
  if (tx.type === 'grant') return tx.note || 'Credit grant';
  if (tx.type === 'purchase') return tx.note || 'Credit purchase';
  if (tx.type === 'refund') return tx.note || 'Refund';
  // spend
  if (tx.status === 'refunded') return 'Generation (refunded)';
  if (tx.status === 'reserved') return 'Generation (pending)';
  return tx.note ?? 'Generation';
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut, updateProfile, updatePassword, changeEmail, deleteAccount } = useAuth();
  const { balance, planTier, subscriptionEndsAt, trialEndsAt, refresh: refreshCredits } = useCredits();

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Change-email (gated verify-to-complete flow). New address + current
  // password (re-auth). changeEmail() sends a confirmation link to the new
  // address; the switch applies only AFTER the user clicks it — the account
  // email never changes on Save.
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Password change — separate state from the profile form so the two
  // sections operate independently (each has its own button, errors, etc).
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwShow, setPwShow] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Delete-account flow — destructive, so it's gated behind a modal that
  // requires the current password (re-auth) + a typed confirmation.
  const [delOpen, setDelOpen] = useState(false);
  const [delPassword, setDelPassword] = useState('');
  const [delConfirm, setDelConfirm] = useState('');
  const [delError, setDelError] = useState('');
  const [delLoading, setDelLoading] = useState(false);

  const closeDeleteModal = () => {
    if (delLoading) return;
    setDelOpen(false);
    setDelPassword('');
    setDelConfirm('');
    setDelError('');
  };

  // Google/phone accounts have no password → confirm via a provider popup
  // instead of a password field.
  const isPasswordUser = user?.providers?.includes('password') ?? false;

  const handleDeleteAccount = async () => {
    if (delConfirm.trim().toUpperCase() !== 'DELETE') {
      setDelError('Type DELETE to confirm.');
      return;
    }
    if (isPasswordUser && !delPassword) {
      setDelError('Enter your password to confirm.');
      return;
    }
    setDelLoading(true);
    setDelError('');
    const { error: delErr } = await deleteAccount(isPasswordUser ? delPassword : undefined);
    if (delErr) {
      setDelError(delErr);
      setDelLoading(false);
      return;
    }
    // Success — the auth account is gone; onAuthStateChanged hard-redirects
    // to sign-in. Navigate home as a fallback in case that hasn't fired yet.
    navigate('/');
  };

  // Phone linking state — 3 modes derived from PhoneStatus + local form:
  //   no phone yet                 → show "add phone" input + Send Code btn
  //   phone set + pending (code sent, awaiting verify) → show 6-digit input
  //   phone set + verified         → show "+1555... [Unlink]" + reminder
  const [phoneState, setPhoneState] = useState<PhoneStatus | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');

  const [recentTx, setRecentTx] = useState<CreditTx[]>([]);
  const [txLoading, setTxLoading] = useState(true);

  // Pull current phone-link status on mount + after any phone action.
  const refreshPhone = useCallback(async () => {
    try {
      const s = await getPhoneStatus();
      setPhoneState(s);
    } catch {
      // Non-fatal — UI just renders the "add phone" state.
      setPhoneState({ phone: null, verified: false, pending: false });
    }
  }, []);

  useEffect(() => {
    void refreshPhone();
  }, [refreshPhone]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneError('');
    setPhoneSuccess('');
    if (!phoneInput.trim()) {
      setPhoneError('Enter a phone number first.');
      return;
    }
    setPhoneBusy(true);
    try {
      await registerPhone(phoneInput.trim());
      setPhoneSuccess('Code sent — check your phone.');
      setPhoneCode('');
      await refreshPhone();
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Could not send code.');
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneError('');
    setPhoneSuccess('');
    if (phoneCode.trim().length !== 6) {
      setPhoneError('Enter the 6-digit code.');
      return;
    }
    setPhoneBusy(true);
    try {
      await verifyPhone(phoneCode.trim());
      setPhoneSuccess('Phone linked. Text any SMS command to start.');
      setPhoneCode('');
      setPhoneInput('');
      await refreshPhone();
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Code did not match.');
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleUnlinkPhone = async () => {
    setPhoneError('');
    setPhoneSuccess('');
    setPhoneBusy(true);
    try {
      await unlinkPhone();
      setPhoneSuccess('Phone unlinked.');
      setPhoneInput('');
      setPhoneCode('');
      await refreshPhone();
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Could not unlink phone.');
    } finally {
      setPhoneBusy(false);
    }
  };

  const justUpgraded = searchParams.get('upgraded') === '1';

  // Pull the last 10 credit movements via the backend (which queries
  // Cloud SQL). Pre-Phase-3 this read Supabase directly under RLS; now
  // every page-visible data path goes through the API so the credit-
  // tracking source of truth stays single.
  const fetchTx = useCallback(async () => {
    setTxLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/credit-transactions?limit=10`, {
        headers: await getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) setRecentTx(data as CreditTx[]);
      }
    } catch {
      // Non-fatal — empty list, page still renders.
    } finally {
      setTxLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCredits();
    fetchTx();
  }, [refreshCredits, fetchTx]);

  useEffect(() => {
    if (justUpgraded) {
      setSuccess('Your subscription has been updated.');
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [justUpgraded]);

  if (!user) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-dark-muted text-sm mb-4">Please sign in to view your profile.</p>
          <a
            href="/auth/sign-in"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
          >
            Sign In
          </a>
        </div>
      </div>
    );
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    // Display name only. Email has its own gated verify-to-complete flow
    // (handleChangeEmail) — it is never changed from this form.
    const result = await updateProfile({ displayName });
    setLoading(false);
    if (result.error) setError(result.error);
    else setSuccess('Profile updated successfully.');
  };

  // Gated email change — re-auth with the current password, then send a
  // confirmation link to the new address. The email switches only after the
  // user clicks that link (Firebase verifyBeforeUpdateEmail); nothing changes
  // here on submit except "we sent the link."
  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    const target = newEmail.trim();
    if (!target) {
      setEmailError('Enter the new email address.');
      return;
    }
    if (target.toLowerCase() === (user?.email ?? '').toLowerCase()) {
      setEmailError('That’s already your email.');
      return;
    }
    if (!emailPw) {
      setEmailError('Enter your current password to confirm.');
      return;
    }
    setEmailLoading(true);
    const result = await changeEmail(target, emailPw);
    setEmailLoading(false);
    if (result.error) {
      setEmailError(result.error);
      return;
    }
    setEmailSuccess(
      `Confirmation link sent to ${target}. Open it to finish the change — your email stays ${user?.email} until you confirm.`
    );
    setNewEmail('');
    setEmailPw('');
    setEmailFormOpen(false);
  };

  // Password change. Supabase's updateUser({ password }) doesn't require the
  // user's current password — the existing valid session is the proof of
  // identity. If you wanted to require re-entering the current password
  // (more conservative UX), call signInWithPassword({ email, currentPassword })
  // first as a sanity check before calling updatePassword. For MVP we trust
  // the session.
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (pwNew.length < 6) {
      setPwError('Password must be at least 6 characters.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError("Passwords don't match.");
      return;
    }

    setPwLoading(true);
    const result = await updatePassword(pwNew);
    setPwLoading(false);

    if (result.error) {
      setPwError(result.error);
      return;
    }

    setPwSuccess('Password updated. Use the new password next time you sign in.');
    setPwNew('');
    setPwConfirm('');
    // Auto-clear the success message after 5s so it doesn't sit there forever
    setTimeout(() => setPwSuccess(''), 5000);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const plan = getPlanInfo(planTier);
  // Progress bar: balance vs monthly allowance. For free tier the allowance is
  // the signup grant (10) so the bar shows "credits remaining of the grant".
  // When balance > allowance (e.g. user has top-up credits beyond their plan's
  // monthly amount), we display the balance straightforwardly and let the bar
  // cap at 100% — top-up surplus is called out separately below the bar.
  const allowance = plan.creditsPerMonth || 1;
  const balanceNum = balance ?? 0;
  const hasTopupSurplus = balanceNum > allowance;
  const surplus = hasTopupSurplus ? balanceNum - allowance : 0;
  // Bar fill: when within plan budget, show "remaining" ratio; when over
  // plan budget (top-up active), bar is 100% full.
  const usedWithinPlan = hasTopupSurplus ? 0 : Math.max(0, allowance - balanceNum);
  const usedPct = hasTopupSurplus ? 100 : Math.min(100, (usedWithinPlan / allowance) * 100);
  const isFree = planTier === 'free';
  // Free-trial state: paid plan_tier (for access) + a future trial_ends_at.
  const isTrialing = !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();
  const trialDaysLeft = isTrialing
    ? Math.max(1, Math.ceil((new Date(trialEndsAt!).getTime() - Date.now()) / 86_400_000))
    : 0;

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-white border-b border-surface-dark/40">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center">
            <Logo height={56} />
          </a>
          <div className="flex items-center gap-2">
            {/* Jun 26 — primary CTA back to app. Strong fill + arrow makes
                this the obvious exit; the tiny ghost link before was easy
                to miss. Label matches the sidebar's "My Creatives" so the
                destination reads consistently across the app. */}
            <a
              href="/gallery"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Back to My Creatives
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 py-10">
        <h1 className="font-display font-bold text-dark text-2xl mb-1">Account</h1>
        <p className="text-dark-muted text-sm mb-8">Manage your profile, plan, and credit usage.</p>

        {error && (
          <div className="mb-5 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-200">
            <i className="ri-error-warning-line" />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-5 flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200">
            <i className="ri-check-line" />
            {success}
          </div>
        )}

        {/* Avatar + Info */}
        <div className="flex items-center gap-4 mb-6 p-4 rounded-2xl bg-white border border-surface-dark/40">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-3-line text-primary text-xl" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-dark text-sm truncate">
              {user.displayName || user.email?.split('@')[0] || 'Creator'}
            </p>
            <p className="text-dark-muted text-xs truncate">{user.email}</p>
          </div>
        </div>

        {/* Plan + credits */}
        <div className="mb-6 p-5 rounded-2xl bg-white border border-surface-dark/40">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-dark text-sm">Plan &amp; Credits</h3>
              <p className="text-dark-muted text-xs">Your current tier and balance</p>
            </div>
            <button
              onClick={() => navigate('/checkout')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-all cursor-pointer"
            >
              {isFree ? <i className="ri-vip-crown-line" /> : <i className="ri-arrow-up-line" />}
              {isFree ? 'Upgrade' : 'Manage Plan'}
            </button>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-xl bg-surface mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${plan.iconBg}`}>
              <i className={plan.icon} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-dark text-sm flex items-center gap-2">
                {plan.label}
                {isTrialing && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wide">
                    <i className="ri-gift-line" />Trial
                  </span>
                )}
              </p>
              <p className="text-dark-muted text-xs">
                {plan.price === 0 ? 'No subscription' : `$${plan.price}/month`}
                {!isFree && plan.creditsPerMonth > 0 && ` · ${plan.creditsPerMonth.toLocaleString()} credits/month`}
              </p>
            </div>
          </div>

          {/* Free-trial notice — while trial_ends_at is in the future. Card
              still shows full plan access; charge lands only at trial end. */}
          {isTrialing && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-primary/5 border border-primary/15 mb-4">
              <i className="ri-gift-line text-primary text-base flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-dark text-xs">
                  Free trial active — {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left
                </p>
                <p className="text-dark-muted text-xs mt-0.5">
                  You have full {plan.label} access. We&apos;ll charge ${plan.price}/month when your
                  trial ends — cancel anytime before then via Manage Plan.
                </p>
              </div>
            </div>
          )}

          {/* Cancellation notice — shown when user has cancelled via Customer
              Portal with "at end of billing period" (the default). They keep
              full access until subscriptionEndsAt; after that, the
              customer.subscription.deleted webhook flips plan_tier to 'free'. */}
          {subscriptionEndsAt && !isFree && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200 mb-4">
              <i className="ri-error-warning-line text-amber-600 text-base flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-amber-900 text-xs">
                  Subscription cancelled
                </p>
                <p className="text-amber-700 text-xs mt-0.5">
                  You'll keep access until{' '}
                  <span className="font-semibold">
                    {new Date(subscriptionEndsAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                  . Reactivate anytime via Manage Plan.
                </p>
              </div>
            </div>
          )}

          <div className="mb-1">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-dark-muted">
                {isFree ? 'Trial credits remaining' : 'Credits available'}
              </span>
              <span className="font-semibold text-dark tabular-nums">
                {balanceNum.toLocaleString()}
                {!isFree && !hasTopupSurplus && (
                  <span className="text-dark-muted font-normal">
                    {' / '}{allowance.toLocaleString()}
                  </span>
                )}
              </span>
            </div>
            <div className="w-full h-2 bg-surface-dark/30 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  hasTopupSurplus ? 'bg-emerald-500'
                  : usedPct > 90 ? 'bg-red-500'
                  : usedPct > 70 ? 'bg-amber-500'
                  : 'bg-primary'
                }`}
                style={{ width: `${usedPct}%` }}
              />
            </div>
            <p className="text-dark-muted text-xs mt-1.5">
              {balanceNum === 0 ? (
                <>No credits left — {isFree ? 'upgrade' : 'top up'} to keep generating.</>
              ) : hasTopupSurplus ? (
                <>
                  <span className="font-semibold text-emerald-700">+{surplus}</span> from
                  top-up · refills to {allowance.toLocaleString()} credits on next renewal.
                </>
              ) : (
                <>{balanceNum} credit{balanceNum === 1 ? '' : 's'} available.</>
              )}
            </p>
          </div>
        </div>

        {/* Jun 26 — Recent activity moved to bottom (below Session/Sign out)
            per user feedback. The placeholder above keeps the visual flow
            consistent for any future debugging — the block now lives at
            the end of <main>. */}

        {/* Profile Form */}
        <form onSubmit={handleUpdate} className="space-y-5 mb-8">
          <div>
            <label htmlFor="displayName" className="block text-sm font-semibold text-dark mb-2">
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <>
                <i className="ri-save-line" />
                Save Changes
              </>
            )}
          </button>
        </form>

        {/* Email — gated verify-to-complete change flow. The address never
            changes on Save; changeEmail() re-auths + sends a link to the new
            address, and the switch applies only after the user clicks it. */}
        <div className="mb-8 p-5 rounded-2xl bg-white border border-surface-dark/40">
          <h3 className="font-display font-bold text-dark text-sm mb-1">Email</h3>
          <p className="text-dark-muted text-xs mb-4">
            Your account email. Changing it requires confirming the new address.
          </p>

          {emailError && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-xs font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              {emailError}
            </div>
          )}
          {emailSuccess && (
            <div className="mb-4 flex items-start gap-2 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
              <i className="ri-mail-send-line mt-0.5 flex-shrink-0" />
              <span>{emailSuccess}</span>
            </div>
          )}

          {/* Current email + verification state */}
          <div className="flex items-center justify-between gap-3 rounded-xl border border-surface-dark/50 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm text-dark truncate">{user.email}</p>
              <p className={`text-[11px] ${user.emailVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
                {user.emailVerified ? 'Verified' : 'Not verified'}
              </p>
            </div>
            {isPasswordUser && !emailFormOpen && (
              <button
                type="button"
                onClick={() => { setEmailFormOpen(true); setEmailError(''); setEmailSuccess(''); }}
                className="flex-shrink-0 px-4 py-2 rounded-full border border-surface-dark text-dark text-xs font-semibold hover:border-primary/40 hover:text-primary transition-colors cursor-pointer"
              >
                Change email
              </button>
            )}
          </div>

          {/* Google-managed accounts can't change email here */}
          {!isPasswordUser && (
            <p className="mt-3 text-xs text-dark-muted">
              Your email is managed by your Google account — change it there.
            </p>
          )}

          {/* Gated change form — new address + current password (re-auth) */}
          {isPasswordUser && emailFormOpen && (
            <form onSubmit={handleChangeEmail} className="mt-4 space-y-4">
              <div>
                <label htmlFor="newEmail" className="block text-xs font-semibold text-dark mb-2">
                  New email
                </label>
                <input
                  id="newEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@newaddress.com"
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
              </div>
              <div>
                <label htmlFor="emailPw" className="block text-xs font-semibold text-dark mb-2">
                  Current password
                </label>
                <input
                  id="emailPw"
                  type="password"
                  value={emailPw}
                  onChange={(e) => setEmailPw(e.target.value)}
                  placeholder="Confirm it's you"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
              </div>
              <p className="text-[11px] text-dark-muted">
                We'll email a confirmation link to the new address. Your email
                changes only after you click it — until then it stays {user.email}.
              </p>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={emailLoading || !newEmail || !emailPw}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {emailLoading ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      Send verification link
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setEmailFormOpen(false); setNewEmail(''); setEmailPw(''); setEmailError(''); }}
                  className="px-5 py-3 rounded-full border border-surface-dark text-dark text-sm font-semibold hover:bg-surface-dark/30 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Change password */}
        <form
          onSubmit={handlePasswordChange}
          className="mb-8 p-5 rounded-2xl bg-white border border-surface-dark/40"
        >
          <h3 className="font-display font-bold text-dark text-sm mb-1">Change password</h3>
          <p className="text-dark-muted text-xs mb-4">
            Set a new password. You'll stay signed in on this device; use the new one next time.
          </p>

          {pwError && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-xs font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              {pwError}
            </div>
          )}
          {pwSuccess && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
              <i className="ri-check-line" />
              {pwSuccess}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="pwNew" className="block text-xs font-semibold text-dark mb-2">
                New password
              </label>
              <div className="relative">
                <input
                  id="pwNew"
                  type={pwShow ? 'text' : 'password'}
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  placeholder="Min. 6 characters"
                  minLength={6}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setPwShow(!pwShow)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-dark-muted hover:text-dark transition-colors cursor-pointer"
                  aria-label={pwShow ? 'Hide password' : 'Show password'}
                >
                  <i className={pwShow ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="pwConfirm" className="block text-xs font-semibold text-dark mb-2">
                Confirm new password
              </label>
              <input
                id="pwConfirm"
                type={pwShow ? 'text' : 'password'}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                placeholder="Re-enter your new password"
                minLength={6}
                autoComplete="new-password"
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={pwLoading || !pwNew || !pwConfirm}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-surface-dark text-dark text-sm font-semibold hover:border-primary/40 hover:text-primary transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {pwLoading ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-dark/20 border-t-dark animate-spin" />
                  Updating…
                </>
              ) : (
                <>
                  <i className="ri-lock-password-line" />
                  Update password
                </>
              )}
            </button>
          </div>
        </form>

        {/* SMS commands — phone linking. HIDDEN: SMS is no longer active.
            Panel gated off (not deleted) — the phone state/handlers/imports are
            left intact so it can be re-enabled by flipping this guard to true. */}
        {false && (
        <div className="p-5 rounded-2xl bg-white border border-surface-dark/40 mb-4">
          <h3 className="font-display font-bold text-dark text-sm mb-1">
            <i className="ri-message-2-line mr-1" />
            SMS commands
          </h3>
          <p className="text-dark-muted text-xs mb-4">
            Link your phone to text the app for quick actions — &ldquo;credits&rdquo;, &ldquo;what brand&rdquo;,
            &ldquo;show last 5 posts&rdquo;.
          </p>

          {phoneError && (
            <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
              {phoneError}
            </div>
          )}
          {phoneSuccess && (
            <div className="mb-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-700">
              {phoneSuccess}
            </div>
          )}

          {phoneState?.verified && phoneState.phone && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-dark">{phoneState.phone}</div>
                <div className="text-xs text-emerald-600">
                  <i className="ri-check-line mr-0.5" />
                  Verified — text SMS commands to your account
                </div>
              </div>
              <button
                onClick={handleUnlinkPhone}
                disabled={phoneBusy}
                className="px-4 py-2 rounded-full border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-all cursor-pointer disabled:opacity-50"
              >
                Unlink
              </button>
            </div>
          )}

          {phoneState && !phoneState.verified && phoneState.pending && (
            <form onSubmit={handleVerifyCode} className="space-y-3">
              <div className="text-xs text-dark-muted">
                Code sent to {phoneState.phone}. Enter the 6-digit code below.
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-2 border border-dark-muted/40 rounded-md text-sm tracking-widest text-center font-mono"
                disabled={phoneBusy}
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={phoneBusy || phoneCode.length !== 6}
                  className="flex-1 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-semibold transition-all disabled:opacity-50 cursor-pointer"
                >
                  {phoneBusy ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={handleUnlinkPhone}
                  disabled={phoneBusy}
                  className="px-4 py-2 rounded-full border border-surface-dark/60 text-dark-muted text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {phoneState && !phoneState.verified && !phoneState.pending && (
            <form onSubmit={handleSendCode} className="space-y-3">
              <input
                type="tel"
                placeholder="+15551234567"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                className="w-full px-3 py-2 border border-dark-muted/40 rounded-md text-sm"
                disabled={phoneBusy}
              />
              <button
                type="submit"
                disabled={phoneBusy || !phoneInput.trim()}
                className="w-full px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-semibold transition-all disabled:opacity-50 cursor-pointer"
              >
                {phoneBusy ? 'Sending…' : 'Send verification code'}
              </button>
              <p className="text-xs text-dark-muted">
                Standard SMS rates from your carrier apply. Starter plan or higher required.
              </p>
            </form>
          )}
        </div>
        )}

        {/* Session */}
        <div className="p-5 rounded-2xl bg-white border border-surface-dark/40">
          <h3 className="font-semibold text-dark text-sm mb-1">Session</h3>
          <p className="text-dark-muted text-xs mb-4">Sign out from your account on this device.</p>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 transition-all cursor-pointer"
          >
            <i className="ri-logout-box-r-line" />
            Sign Out
          </button>
        </div>

        {/* Recent activity — Jun 26: moved below Session per user feedback.
            Lives at the bottom of the page as a passive reference, not a
            primary action. mt-8 spaces it from Sign Out without crowding. */}
        <div className="mt-8 p-5 rounded-2xl bg-white border border-surface-dark/40">
          <h3 className="font-display font-bold text-dark text-sm mb-1">Recent activity</h3>
          <p className="text-dark-muted text-xs mb-4">Your latest credit movements</p>

          {txLoading ? (
            <div className="py-8 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
            </div>
          ) : recentTx.length === 0 ? (
            <p className="text-dark-muted text-xs italic">No activity yet — your first generation will appear here.</p>
          ) : (
            <ul className="divide-y divide-surface-dark/30">
              {recentTx.map((tx) => {
                const isPositive = tx.amount > 0;
                const isRefunded = tx.type === 'spend' && tx.status === 'refunded';
                return (
                  <li key={tx.id} className="py-3 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm ${
                      isPositive ? 'bg-emerald-50 text-emerald-600' :
                      isRefunded ? 'bg-amber-50 text-amber-600' :
                      'bg-surface text-dark-muted'
                    }`}>
                      <i className={
                        tx.type === 'grant' ? 'ri-gift-line' :
                        tx.type === 'purchase' ? 'ri-shopping-cart-line' :
                        tx.type === 'refund' ? 'ri-arrow-go-back-line' :
                        isRefunded ? 'ri-arrow-go-back-line' :
                        'ri-magic-line'
                      } />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-dark truncate">
                        {describeTx(tx)}
                      </p>
                      <p className="text-xs text-dark-muted">{formatRelative(tx.created_at)}</p>
                    </div>
                    <span className={`text-xs font-bold flex-shrink-0 ${
                      isPositive ? 'text-emerald-600' :
                      isRefunded ? 'text-dark-muted line-through' :
                      'text-dark'
                    }`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Danger Zone — permanent account + data deletion. */}
        <div className="mt-8 p-5 rounded-2xl bg-red-50/60 border border-red-200">
          <h3 className="font-display font-bold text-red-700 text-sm mb-1">Delete account</h3>
          <p className="text-red-700/70 text-xs mb-4">
            Permanently delete your account and everything in it — creatives,
            products, brands, scheduled posts, and connected accounts. This
            cannot be undone.
          </p>
          <button
            onClick={() => setDelOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-all cursor-pointer"
          >
            <i className="ri-delete-bin-line" />
            Delete my account
          </button>
        </div>
      </main>

      {/* Delete-account confirmation modal — requires the password (re-auth)
          + a typed "DELETE" before the destructive call fires. */}
      {delOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark/40 backdrop-blur-sm"
          onMouseDown={closeDeleteModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-600 text-lg" />
              </div>
              <div>
                <h3 className="font-display font-bold text-dark text-base">Delete your account?</h3>
                <p className="text-dark-muted text-xs mt-1">
                  This permanently deletes your account and all your data —
                  creatives, products, brands, scheduled posts, and connected
                  accounts. It cannot be undone.
                </p>
              </div>
            </div>

            {isPasswordUser ? (
              <>
                <label className="block text-xs font-semibold text-dark mb-1">Your password</label>
                <input
                  type="password"
                  value={delPassword}
                  onChange={(e) => setDelPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoFocus
                  className="w-full mb-3 px-4 py-2.5 rounded-xl border border-surface-dark/50 text-sm focus:outline-none focus:border-primary"
                />
              </>
            ) : (
              <p className="text-dark-muted text-xs mb-3">
                You&apos;ll be asked to sign in with Google again to confirm.
              </p>
            )}

            <label className="block text-xs font-semibold text-dark mb-1">
              Type <span className="font-bold">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={delConfirm}
              onChange={(e) => setDelConfirm(e.target.value)}
              placeholder="DELETE"
              className="w-full mb-3 px-4 py-2.5 rounded-xl border border-surface-dark/50 text-sm focus:outline-none focus:border-primary"
            />

            {delError && <p className="text-red-600 text-xs mb-3">{delError}</p>}

            <div className="flex gap-3 mt-2">
              <button
                onClick={closeDeleteModal}
                disabled={delLoading}
                className="flex-1 px-5 py-2.5 rounded-full border border-surface-dark/50 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={delLoading}
                className="flex-1 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {delLoading ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Deleting…
                  </>
                ) : (
                  'Delete forever'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
